/**
 * **What already holds the trainer's time** — the one reading a new session, a moved one and every
 * member of a new series is checked against before it is sent.
 *
 * A session holds its hour while it can still happen or is happening: `Scheduled` (upcoming) or
 * `InProgress`. Anything settled, missed or called off has let its hour go, and an occurrence with
 * no hour holds none. That is the backend's own reading for the trainer's calendar (the double-
 * booking constraint covers live rows only), so the warning here names what the server will see.
 *
 * The warning is NEUTRAL on purpose. The server refuses a single session over another trainer-made
 * or client-made booking (`slot_taken`) but lets it overlap a plan occurrence or a series member,
 * and a series is never refused for a conflict at all (recurring-sessions R9). Which of those it
 * will be is the server's to say; this says that the time is taken, and by whom.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import { isDrawn, type BookingSession } from './bookingSession';
import { blockSpanOn, type TrainerBlock, type WorkingDay } from './workingHours';
import { wallReading, type CivilDay } from './trainerClock';

/** A span of real time, `[startMs, endMs)`. */
export interface TimeSpan {
  readonly startMs: number;
  readonly endMs: number;
}

/** Whether the session still holds its hour on the trainer's calendar. */
export function holdsItsHour(session: BookingSession): boolean {
  return (
    isDrawn(session) &&
    session.hasHour &&
    (session.status === 'scheduled' || session.status === 'live')
  );
}

function overlaps(a: TimeSpan, b: TimeSpan): boolean {
  return a.startMs < b.endMs && b.startMs < a.endMs;
}

/**
 * The sessions holding time inside `span`, earliest first. `exceptId` leaves out the session being
 * moved, which cannot collide with itself.
 */
export function sessionsOverlapping(
  sessions: readonly BookingSession[],
  span: TimeSpan,
  exceptId: number | null = null,
): BookingSession[] {
  return sessions
    .filter(
      (session) =>
        session.id !== exceptId &&
        holdsItsHour(session) &&
        session.startMs !== null &&
        session.endMs !== null &&
        overlaps(span, { startMs: session.startMs, endMs: session.endMs }),
    )
    .sort((a, b) => (a.startMs ?? 0) - (b.startMs ?? 0));
}

/** The sessions holding an hour on `day` — the mover's picture of the day it is offering. */
export function sessionsHoldingOn(
  sessions: readonly BookingSession[],
  day: CivilDay,
): BookingSession[] {
  return sessions.filter((session) => session.day === day && holdsItsHour(session));
}

/**
 * Whether `span` falls outside the trainer's working hours on its day — outside every window, or
 * across one of their blocks (R3, R4). `null` when the day's hours are not known here: saying
 * nothing is better than a note that may be wrong.
 *
 * Allowed either way — the trainer may book any hour they like; the note tells them the client
 * could not have taken it themselves.
 */
export function isOutsideHours(
  span: TimeSpan,
  working: WorkingDay | null,
  blocks: readonly TrainerBlock[],
  zone: string,
): boolean | null {
  if (working === null) return null;
  const start = wallReading(span.startMs, zone);
  const end = wallReading(span.endMs, zone);
  // Ending exactly at midnight is the end of its own day; running past it leaves every window.
  const endsAtMidnight = end.day > start.day && end.minutes === 0;
  if (end.day !== start.day && !endsAtMidnight) return true;
  const endMinutes = endsAtMidnight ? 1440 : end.minutes;
  const inWindow = working.windows.some(
    (window) => window.start <= start.minutes && endMinutes <= window.end,
  );
  if (!inWindow) return true;
  return blocks.some((block) => overlaps(span, block));
}

/** The minutes of `day` the trainer's blocks cover, as the free-hour search reads them. */
export function blockedSpansOn(
  blocks: readonly TrainerBlock[],
  day: CivilDay,
  zone: string,
): { readonly start: number; readonly end: number }[] {
  return blocks.flatMap((block) => {
    const span = blockSpanOn(block, day, zone);
    return span === null ? [] : [span];
  });
}
